/**
 * Cycle Progress Store
 * 
 * Reads from the cycle_progress SQL VIEW - the authoritative read-model for
 * cycle state. This replaces ad-hoc UI store queries with database-derived truth.
 * 
 * Per the Authoritative Cycles implementation plan:
 * - Observed phase = highest phase whose ENTER gates are satisfied
 * - Gates predicate on normalized tables (cycle_participants, etc.)
 * - Ball holder derived from catchball_loops state
 * 
 * The UI should use this store for all cycle state decisions instead of
 * querying degapProcessStore directly.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import type { DegapPhase } from '../../types/prioritization/degapCycle';

// ============================================================================
// Types
// ============================================================================

export interface CycleProgress {
  cycleId: string;
  cycleName: string;
  declaredPhase: DegapPhase;
  observedPhase: DegapPhase;
  cycleStatus: string;
  ownerId: string | null;
  workspaceId: string | null;
  
  // Current Loop context
  currentLoopId: string | null;
  currentLoopNumber: number | null;
  loopState: string | null;
  ballHolderScope: 'executive' | 'middle' | 'bottom' | 'system';
  ballHolderUserId: string | null;
  
  // Gate flags (can-enter booleans)
  canEnterEngage: boolean;
  canEnterGather: boolean;
  canEnterArrange: boolean;
  canEnterPrioritize: boolean;
  canComplete: boolean;
  
  // Gate counts (for UI display/debugging)
  gateDOwner: boolean;
  activeParticipantCount: number;
  activeParticipantsWithRights: number;
  confirmedItemCount: number;
  criteriaAttributeCount: number;  // NEW: count of selected criteria
  criteriaLocked: boolean;
  rankingSessionsComplete: number;
  snapshotCount: number;
  itemPoolCount: number;
  ratifiedSetCount: number;
  
  // Gather readiness sub-gates (per Doc 04 Section 4.2)
  gateGItemsGathered: boolean;
  gateGAttributesSelected: boolean;
  gateGItemPoolReady: boolean;
  
  // Scale readiness - NOTE: These are computed client-side, not from DB VIEW
  // The cycle_progress VIEW does not have these columns
  criteriaScalesAcceptedCount: number;
  criteriaScalesPendingCount: number;
  criteriaMissingScales: string[] | null;
  criteriaScalesReady: boolean;
  gateGScalesReady: boolean;
  
  // Arrange readiness sub-gates (per Doc 04 Section 6 - A→P gate)
  poolItemCount: number;
  poolLocked: boolean;
  gateACriteriaLocked: boolean;
  gateAPoolSelected: boolean;
  gateAPoolLocked: boolean;

  // The same gates kept per EGAp iteration (a cycle may settle a stage in either place)
  egapCriteriaLocked: boolean;
  egapValuesConverged: boolean;
  egapRankingSessionsComplete: number;

  updatedAt: Date;
}

interface CycleProgressState {
  progressByPcycleId: Record<string, CycleProgress>;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchProgress: (cycleId: string) => Promise<CycleProgress | null>;
  fetchAllProgress: () => Promise<void>;
  
  // Selectors
  getProgress: (cycleId: string) => CycleProgress | undefined;
  getPhaseDrift: (cycleId: string) => boolean;
  getBlockers: (cycleId: string) => string[];
}

// ============================================================================
// Database Helpers
// ============================================================================

function dbToProgress(row: Record<string, unknown>): CycleProgress {
  // Defensive boolean coercion - DB may return NULL for computed booleans
  const toBool = (val: unknown): boolean => val === true;
  const toNum = (val: unknown): number => typeof val === 'number' ? val : 0;
  
  return {
    cycleId: row.cycle_id as string,
    cycleName: row.cycle_name as string,
    declaredPhase: row.declared_phase as DegapPhase,
    observedPhase: row.observed_phase as DegapPhase,
    cycleStatus: row.cycle_status as string,
    ownerId: row.owner_id as string | null,
    workspaceId: row.workspace_id as string | null,
    
    currentLoopId: row.current_loop_id as string | null,
    currentLoopNumber: toNum(row.current_loop_number) || null,
    loopState: row.loop_state as string | null,
    // DISPLAY-ONLY (ADR-catchball-consolidation). ball_holder_scope on the
    // cycle_progress view is the legacy three-tier ternary. Authority =
    // catchball_active_branch_positions via getActivePositions(loopId).
    // This field is retained only for coarse progress labels.
    ballHolderScope: (row.ball_holder_scope as 'executive' | 'middle' | 'bottom' | 'system') || 'system',
    ballHolderUserId: row.ball_holder_user_id as string | null,
    
    // Gate flags - use strict boolean coercion to handle NULL
    canEnterEngage: toBool(row.can_enter_engage),
    canEnterGather: toBool(row.can_enter_gather),
    canEnterArrange: toBool(row.can_enter_arrange),
    canEnterPrioritize: toBool(row.can_enter_prioritize),
    canComplete: toBool(row.can_complete),
    
    // Gate counts - use numeric coercion to handle NULL
    gateDOwner: toBool(row.gate_d_owner),
    activeParticipantCount: toNum(row.active_participant_count),
    activeParticipantsWithRights: toNum(row.active_participants_with_rights),
    confirmedItemCount: toNum(row.confirmed_item_count),
    criteriaAttributeCount: toNum(row.criteria_attribute_count),
    criteriaLocked: toBool(row.criteria_locked),
    rankingSessionsComplete: toNum(row.ranking_sessions_complete),
    snapshotCount: toNum(row.snapshot_count),
    itemPoolCount: toNum(row.item_pool_count),
    ratifiedSetCount: toNum(row.ratified_set_count),
    
    // Gather readiness sub-gates
    gateGItemsGathered: toBool(row.gate_g_items_gathered),
    gateGAttributesSelected: toBool(row.gate_g_attributes_selected),
    gateGItemPoolReady: toBool(row.gate_g_item_pool_ready),
    
    // Scale readiness - NOT in DB VIEW, computed client-side
    // These default to 0/false - UI components that need scale data should
    // fetch from cycle_attribute_configs via attributeGovernanceStore
    criteriaScalesAcceptedCount: 0,
    criteriaScalesPendingCount: 0,
    criteriaMissingScales: null,
    criteriaScalesReady: false,
    gateGScalesReady: false,
    
    // Arrange readiness sub-gates (A→P gate)
    poolItemCount: toNum(row.pool_item_count),
    poolLocked: toBool(row.pool_locked),
    gateACriteriaLocked: toBool(row.gate_a_criteria_locked),
    gateAPoolSelected: toBool(row.gate_a_pool_selected),
    gateAPoolLocked: toBool(row.gate_a_pool_locked),

    egapCriteriaLocked: toBool(row.egap_criteria_locked),
    egapValuesConverged: toBool(row.egap_values_converged),
    egapRankingSessionsComplete: toNum(row.egap_ranking_sessions_complete),

    updatedAt: new Date(row.updated_at as string),
  };
}

// ============================================================================
// Store
// ============================================================================

export const useCycleProgressStore = create<CycleProgressState>()((set, get) => ({
  progressByPcycleId: {},
  isLoading: false,
  error: null,

  fetchProgress: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      console.warn('[CycleProgressStore] Supabase not available');
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('cycle_progress')
        .select('*')
        .eq('cycle_id', cycleId)
        .maybeSingle();

      if (error) {
        console.error('[CycleProgressStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return null;
      }

      // No progress record exists yet for this cycle - this is normal for new cycles
      if (!data) {
        set({ isLoading: false, error: null });
        return null;
      }

      const progress = dbToProgress(data);

      set(state => ({
        progressByPcycleId: {
          ...state.progressByPcycleId,
          [cycleId]: progress,
        },
        isLoading: false,
        error: null,
      }));

      console.log(`[CycleProgressStore] Fetched progress for cycle ${cycleId}:`, {
        declared: progress.declaredPhase,
        observed: progress.observedPhase,
        drift: progress.declaredPhase !== progress.observedPhase,
      });

      return progress;
    } catch (err) {
      console.error('[CycleProgressStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
      return null;
    }
  },

  fetchAllProgress: async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      console.warn('[CycleProgressStore] Supabase not available');
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('cycle_progress')
        .select('*');

      if (error) {
        console.error('[CycleProgressStore] Fetch all error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const progressMap: Record<string, CycleProgress> = {};
      for (const row of data || []) {
        const progress = dbToProgress(row);
        progressMap[progress.cycleId] = progress;
      }

      set({
        progressByPcycleId: progressMap,
        isLoading: false,
        error: null,
      });

      console.log(`[CycleProgressStore] Fetched progress for ${Object.keys(progressMap).length} cycles`);
    } catch (err) {
      console.error('[CycleProgressStore] Fetch all exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  // Selectors
  getProgress: (cycleId) => {
    return get().progressByPcycleId[cycleId];
  },

  getPhaseDrift: (cycleId) => {
    const progress = get().progressByPcycleId[cycleId];
    if (!progress) return false;
    return progress.declaredPhase !== progress.observedPhase;
  },

  getBlockers: (cycleId) => {
    const progress = get().progressByPcycleId[cycleId];
    if (!progress) return [];

    const blockers: string[] = [];

    // Check what's blocking advancement based on current declared phase
    switch (progress.declaredPhase) {
      case 'DECIDE':
        if (!progress.gateDOwner) {
          blockers.push('Cycle owner must be assigned');
        }
        break;

      case 'ENGAGE':
        if (progress.activeParticipantCount < 1) {
          blockers.push('At least 1 active participant required');
        }
        if (progress.activeParticipantsWithRights < 1) {
          blockers.push('At least 1 participant must have decision rights');
        }
        break;

      case 'GATHER':
        if (progress.confirmedItemCount < 1) {
          blockers.push('At least 1 confirmed item required');
        }
        if (!progress.gateGAttributesSelected) {
          blockers.push('At least 1 criterion must be selected');
        }
        if (!progress.gateGScalesReady && progress.criteriaScalesPendingCount > 0) {
          blockers.push(`Scale not defined for ${progress.criteriaScalesPendingCount} criteria`);
        }
        if (!progress.criteriaLocked) {
          blockers.push('Criteria must be locked');
        }
        break;

      case 'ARRANGE':
        if (!progress.gateACriteriaLocked) {
          blockers.push('Criteria must be locked');
        }
        if (!progress.gateAPoolSelected) {
          blockers.push('Item Pool must be selected');
        }
        if (!progress.gateAPoolLocked) {
          blockers.push('Item Pool must be locked for ranking');
        }
        break;

      case 'PRIORITIZE':
        if (progress.ratifiedSetCount < 1) {
          blockers.push('Priority list must be ratified or published');
        }
        break;
    }

    return blockers;
  },
}));

// ============================================================================
// React Hook for convenient usage
// ============================================================================

import { useEffect, useMemo, useCallback } from 'react';

/**
 * Hook to fetch and subscribe to cycle progress.
 * Automatically fetches on mount and returns the progress.
 */
export function useCycleProgress(cycleId: string | undefined) {
  const progressMap = useCycleProgressStore(state => state.progressByPcycleId);
  const isLoading = useCycleProgressStore(state => state.isLoading);
  const error = useCycleProgressStore(state => state.error);
  const fetchProgress = useCycleProgressStore(state => state.fetchProgress);

  // Memoize progress lookup to avoid new object references
  const progress = useMemo(() => 
    cycleId ? progressMap[cycleId] : undefined,
    [cycleId, progressMap]
  );

  // Memoize blockers computation - only recompute when progress changes
  const blockers = useMemo(() => {
    if (!progress) return [];
    
    const result: string[] = [];
    switch (progress.declaredPhase) {
      case 'DECIDE':
        if (!progress.gateDOwner) result.push('Cycle owner must be assigned');
        break;
      case 'ENGAGE':
        if (progress.activeParticipantCount < 1) result.push('At least 1 active participant required');
        if (progress.activeParticipantsWithRights < 1) result.push('At least 1 participant must have decision rights');
        break;
      case 'GATHER':
        if (progress.confirmedItemCount < 1) result.push('At least 1 confirmed item required');
        if (!progress.gateGAttributesSelected) result.push('At least 1 criterion must be selected');
        if (!progress.gateGScalesReady && progress.criteriaScalesPendingCount > 0) {
          result.push(`Scale not defined for ${progress.criteriaScalesPendingCount} criteria`);
        }
        if (!progress.criteriaLocked) result.push('Criteria must be locked');
        break;
      case 'ARRANGE':
        if (!progress.gateACriteriaLocked) result.push('Criteria must be locked');
        if (!progress.gateAPoolSelected) result.push('Item Pool must be selected');
        if (!progress.gateAPoolLocked) result.push('Item Pool must be locked for ranking');
        break;
      case 'PRIORITIZE':
        if (progress.ratifiedSetCount < 1) result.push('Priority list must be ratified or published');
        break;
    }
    return result;
  }, [progress]);

  // Memoize phaseDrift
  const phaseDrift = useMemo(() => 
    progress ? progress.declaredPhase !== progress.observedPhase : false,
    [progress]
  );

  // Memoize refresh callback
  const refresh = useCallback(() => {
    if (cycleId) fetchProgress(cycleId);
  }, [cycleId, fetchProgress]);

  useEffect(() => {
    if (cycleId) {
      fetchProgress(cycleId);
    }
  }, [cycleId, fetchProgress]);

  return {
    progress,
    isLoading,
    error,
    blockers,
    phaseDrift,
    refresh,
  };
}
